import type React from "react";
import {useEffect, useRef, useState} from "react";
import {Button, Skeleton} from "@mui/material";
import {embedDashboard} from "@superset-ui/embedded-sdk";
import {useStores} from "../../stores";

type Props = {
    embeddedUuid: string;
    supersetDomain: string;
};

type Status = "loading" | "ready" | "error";

/**
 * Embedded Superset dashboard filling its container. Guest tokens come from the
 * agent (`GET /superset/guest-token`), which checks access first; the SDK
 * re-fetches them before they expire. Superset must allow this origin in
 * frame-ancestors or the iframe is blocked.
 */
export const SupersetEmbed: React.FC<Props> = ({embeddedUuid, supersetDomain}) => {
    const {chatApiStore} = useStores();
    const mountRef = useRef<HTMLDivElement>(null);
    const [status, setStatus] = useState<Status>("loading");
    const [error, setError] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        const mountPoint = mountRef.current;
        if (!mountPoint) return;

        let disposed = false;
        let unmount: (() => void) | undefined;
        setStatus("loading");
        setError(null);

        embedDashboard({
            id: embeddedUuid,
            supersetDomain,
            mountPoint,
            fetchGuestToken: () => chatApiStore.getSupersetGuestToken(embeddedUuid),
            dashboardUiConfig: {hideTitle: true, filters: {expanded: true}},
        }).then(embedded => {
            if (disposed) {
                embedded.unmount();
                return;
            }
            unmount = embedded.unmount;
            // The SDK resolves only after the iframe's load event and the guest-token
            // handshake, so the dashboard is already loading its charts here.
            const iframe = mountPoint.querySelector("iframe");
            if (iframe) iframe.title = "Superset dashboard";
            setStatus("ready");
        }).catch((err: unknown) => {
            console.error("[SupersetEmbed] Failed to embed dashboard:", err);
            if (disposed) return;
            setError(err instanceof Error ? err.message : "Could not load the dashboard.");
            setStatus("error");
        });

        return () => {
            disposed = true;
            try {
                unmount?.();
            } catch {
                /* iframe already removed */
            }
            mountPoint.innerHTML = "";
        };
    }, [embeddedUuid, supersetDomain, chatApiStore, attempt]);

    return (
        <div className="dsh-embed">
            <div ref={mountRef} className="dsh-embed-mount" data-status={status}/>
            {status === "loading" && (
                <div className="dsh-embed-skeleton" aria-label="Loading dashboard">
                    <Skeleton variant="rounded" height={32} width="40%"/>
                    <div className="dsh-embed-skeleton-row">
                        {[0, 1, 2, 3].map(i => <Skeleton key={i} variant="rounded" height={88}/>)}
                    </div>
                    <div className="dsh-embed-skeleton-row dsh-embed-skeleton-row--2">
                        <Skeleton variant="rounded" height={240}/>
                        <Skeleton variant="rounded" height={240}/>
                    </div>
                </div>
            )}
            {status === "error" && (
                <div className="dsh-embed-error" role="alert">
                    <p className="dsh-t-card-title">This dashboard could not be loaded</p>
                    <p className="dsh-t-body-sm">{error}</p>
                    <Button variant="outlined" size="small" onClick={() => setAttempt(a => a + 1)}>Retry</Button>
                </div>
            )}
        </div>
    );
};
